/**
 * Il vocabolario della moderazione e del debriefing.
 *
 * Sono elenchi chiusi, e stanno qui e non in una rotta per la stessa ragione per
 * cui ci stanno le fasce di apertura: li usa il server per validare, e i test
 * dei vincoli per verificare che ogni voce abbia una traduzione in tutte le
 * lingue.
 *
 * **Lo snippet ne tiene ancora una copia** (`VOLUNTEER_REPORT_REASONS`, in
 * `apps/snippet/src/shared/report.ts`), e non per distrazione: l'indice di
 * questo pacchetto tira dentro `node:crypto` (`secret-box`, `password`), che
 * in un bundle per il browser non ha niente da fare. Un test dei vincoli
 * impedisce a quella copia di divergere (`tests/constraints/moderation.test.ts`),
 * perché una voce aggiunta da una parte sola diventerebbe un motivo che il
 * server rifiuta, scoperto in produzione.
 *
 * **La console non tiene più una copia**: importa da `@platform/core/moderation`,
 * un subpath export che non passa dall'indice del pacchetto (quindi non tira
 * `node:crypto` nel bundle della dashboard) e resta l'unica fonte per
 * `AGE_BANDS`, `NOTE_TOPICS` e `USER_REPORT_REASONS` (`Debrief.tsx`).
 *
 * I valori sono **chiavi**, mai testo: quello sta nei file di traduzione
 * (sezione 4).
 */

/**
 * Perché un utente segnala un volontario (sezione 7quater).
 *
 * L'elenco è chiuso e non un campo libero: chi segnala sta uscendo da una
 * conversazione difficile, e chiedergli di formulare un'accusa è chiedergli di
 * riviverla. Il testo resta possibile, facoltativo, dopo la scelta.
 *
 * Rispecchia l'enumerazione PostgreSQL `volunteer_report_reason`: un valore che
 * non è qui lo rifiuta anche il database (`docs/DECISIONI.md`, E7).
 */
export const VOLUNTEER_REPORT_REASONS = [
  /** Modi bruschi, giudizio, mancanza di rispetto. */
  'disrespect',
  /** Insistenza dopo una richiesta di smettere. */
  'pressure',
  /** Contenuto o allusioni sessuali. */
  'sexual',
  /** Ha chiesto un contatto fuori dal servizio. */
  'personal_contact',
  /** Ha suggerito qualcosa di pericoloso. */
  'unsafe_advice',
  'other',
] as const;

export type VolunteerReportReason = (typeof VOLUNTEER_REPORT_REASONS)[number];

/** Perché un volontario segnala un utente. Elenco distinto: è un'altra cosa. */
export const USER_REPORT_REASONS = [
  /** Insulti, minacce, aggressività verso chi ha risposto. */
  'abuse',
  'sexual',
  /** Uso ripetuto e improprio del servizio. */
  'repeated_misuse',
  'other',
] as const;

export type UserReportReason = (typeof USER_REPORT_REASONS)[number];

/**
 * Fasce d'età del debriefing.
 *
 * "I dati anagrafici sono stimati o auto-riferiti, mai verificati: è così che
 * lavorano tutte le helpline, e i report devono dichiararlo" (sezione 5.8).
 * `unknown` non è una voce di ripiego: su una chiamata anonima è spesso la
 * risposta vera, e toglierla farebbe scegliere a caso.
 */
export const AGE_BANDS = ['under_18', '18_25', '26_40', '41_65', 'over_65', 'unknown'] as const;

export type AgeBand = (typeof AGE_BANDS)[number];

/**
 * Temi prevalenti.
 *
 * Restano un elenco in codice e non un tipo PostgreSQL, a differenza del
 * livello di rischio: un tema è una categoria di rendicontazione e cambia con i
 * bandi, mentre il livello di rischio decide un comportamento
 * (`docs/DECISIONI.md`, AC13).
 */
export const NOTE_TOPICS = [
  'loneliness',
  'family',
  'relationships',
  'health',
  'work_money',
  'grief',
  'violence',
  'self_harm',
  /** Molto comune in questo tipo di associazioni: sempre un dato di rendicontazione, mai una segnalazione. */
  'prank_call',
  'other',
] as const;

export type NoteTopic = (typeof NOTE_TOPICS)[number];

/** Livelli di rischio del debriefing. Rispecchia l'enumerazione `risk_level`. */
export const RISK_LEVELS = ['low', 'follow_up', 'critical'] as const;

export type RiskLevel = (typeof RISK_LEVELS)[number];

/**
 * I livelli sopra i quali il testo libero è ammesso.
 *
 * "Il testo libero compare solo se il volontario segna «da seguire» o
 * «critica»" (sezione 5.8). La regola vive qui e la applica la rotta, non la
 * schermata: è l'unico posto del prodotto in cui può finire qualcosa su una
 * persona, e un limite che vive nel client si aggira con una richiesta scritta
 * a mano (R12).
 */
export const RISK_LEVELS_WITH_TEXT: readonly RiskLevel[] = ['follow_up', 'critical'];

export function allowsFreeText(level: RiskLevel | null | undefined): boolean {
  return level !== null && level !== undefined && RISK_LEVELS_WITH_TEXT.includes(level);
}

/**
 * Quanti passi può avere un protocollo di crisi, e quanto lungo può essere uno.
 *
 * Non sono soglie di prodotto — quelle stanno nell'ambiente — ma limiti di
 * forma, come il numero massimo di fasce di apertura: un protocollo di quaranta
 * passi non si legge mentre qualcuno sta parlando di farsi del male, ed è
 * proprio quello il momento in cui va letto.
 */
export const MAX_PROTOCOL_STEPS = 12;
export const MAX_PROTOCOL_STEP_LENGTH = 300;

/**
 * Quanto può essere lungo il motivo scritto a mano su un'allerta supervisore.
 *
 * Corto di proposito: è la prima riga che il reperibile legge sul telefono, e
 * si scrive con una persona in linea.
 */
export const MAX_ESCALATION_REASON_LENGTH = 500;

/** Quanto può essere lungo il testo facoltativo di una segnalazione. */
export const MAX_REPORT_TEXT_LENGTH = 2000;
